import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from 'config/googleMaps';

const GOOGLE_MAPS_SCRIPT_BASE_URL = 'https://maps.googleapis.com/maps/api/js';
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-shared-route-api';

let googleMapsApiPromise = null;

const getGoogleMapsKey = () => GOOGLE_MAPS_KEY || '';

const normalizePoint = (point) => {
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
};

export const loadGoogleMapsApi = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Maps API is only available in the browser.'));
  }

  if (window.google?.maps?.DirectionsService) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsApiPromise) {
    return googleMapsApiPromise;
  }

  const apiKey = getGoogleMapsKey();
  if (!apiKey) {
    return Promise.reject(new Error('VITE_GOOGLE_MAPS_KEY is not configured.'));
  }

  googleMapsApiPromise = new Promise((resolve, reject) => {
    // Reaproveita qualquer script da API já presente na página (o injetado por
    // este util, pelo @react-google-maps/api ou pelo google-map-react). Carregar
    // a API duas vezes faz o Google descartar o segundo carregamento e pode
    // deixar libraries de fora.
    const existingScript =
      document.getElementById(GOOGLE_MAPS_SCRIPT_ID) ||
      document.querySelector(`script[src*="${GOOGLE_MAPS_SCRIPT_BASE_URL}"]`);
    if (existingScript) {
      if (window.google?.maps) {
        resolve(window.google.maps);
        return;
      }
      existingScript.addEventListener('load', () => resolve(window.google.maps), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps API.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `${GOOGLE_MAPS_SCRIPT_BASE_URL}?key=${encodeURIComponent(
      apiKey,
    )}&libraries=${GOOGLE_MAPS_LIBRARIES.join(',')}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error('Failed to load Google Maps API.'));
    document.head.appendChild(script);
  });

  return googleMapsApiPromise;
};

const flattenGoogleSteps = (steps = []) => {
  const coordinates = [];

  const appendLatLng = (latLng) => {
    const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
    const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const nextCoordinate = [lat, lng];
    const lastCoordinate = coordinates[coordinates.length - 1];

    if (!lastCoordinate || lastCoordinate[0] !== nextCoordinate[0] || lastCoordinate[1] !== nextCoordinate[1]) {
      coordinates.push(nextCoordinate);
    }
  };

  const walkSteps = (stepList) => {
    (stepList || []).forEach(step => {
      if (Array.isArray(step?.steps) && step.steps.length > 0) {
        walkSteps(step.steps);
      }

      if (Array.isArray(step?.path) && step.path.length > 0) {
        step.path.forEach(appendLatLng);
        return;
      }

      if (step?.start_location) appendLatLng(step.start_location);
      if (step?.end_location) appendLatLng(step.end_location);
    });
  };

  walkSteps(steps);
  return coordinates;
};

const extractGoogleRouteCoordinates = (route) => {
  const denseCoordinates = [];

  (route?.legs || []).forEach((leg) => {
    const legCoordinates = flattenGoogleSteps(leg?.steps || []);

    if (denseCoordinates.length === 0) {
      denseCoordinates.push(...legCoordinates);
      return;
    }

    if (legCoordinates.length > 0) {
      const [, ...rest] = legCoordinates;
      denseCoordinates.push(...rest);
    }
  });

  if (denseCoordinates.length >= 2) {
    return denseCoordinates;
  }

  return (route?.overview_path || [])
    .map(latLng => {
      const lat = typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;
      const lng = typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;
      return [lat, lng];
    })
    .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
};

const defaultCacheKey = (points = []) =>
  points
    .map(point => `${Number(point.lat).toFixed(6)}:${Number(point.lng).toFixed(6)}`)
    .join('|');

export const calculateGoogleRoute = async ({
  points = [],
  cache = null,
  cacheKey = null,
  isCurrentRequest = null,
  waypointStopover = true,
  optimizeWaypoints = false,
} = {}) => {
  const normalizedPoints = (points || [])
    .map(normalizePoint)
    .filter(Boolean);

  if (normalizedPoints.length < 2) {
    throw new Error('At least two valid points are required to calculate the route.');
  }

  const effectiveCacheKey = cacheKey || defaultCacheKey(normalizedPoints);
  if (cache && cache.has(effectiveCacheKey)) {
    return cache.get(effectiveCacheKey);
  }

  const maps = await loadGoogleMapsApi();
  if (typeof isCurrentRequest === 'function' && !isCurrentRequest()) return null;

  const directionsService = new maps.DirectionsService();
  const [origin, ...remainingPoints] = normalizedPoints;
  const destination = remainingPoints.pop();

  const googleResult = await directionsService.route({
    origin,
    destination,
    waypoints: remainingPoints.map(location => ({
      location,
      stopover: waypointStopover,
    })),
    optimizeWaypoints,
    provideRouteAlternatives: false,
    travelMode: maps.TravelMode.DRIVING,
  });

  if (typeof isCurrentRequest === 'function' && !isCurrentRequest()) return null;

  const route = googleResult?.routes?.[0];
  if (!route) {
    throw new Error('Google Directions did not return a route.');
  }

  const coordinates = extractGoogleRouteCoordinates(route);
  if (coordinates.length < 2) {
    throw new Error('Google Directions returned an empty route geometry.');
  }

  const result = {
    coordinates,
    totalDistance: (route.legs || []).reduce(
      (total, leg) => total + Number(leg?.distance?.value || 0),
      0,
    ),
    totalTime: (route.legs || []).reduce(
      (total, leg) => total + Number(leg?.duration?.value || 0),
      0,
    ),
  };

  if (cache) {
    cache.set(effectiveCacheKey, result);
  }

  return result;
};
